// components
import DayHeader from '../../components/DayHeader';
import DayCell from '../../components/DayCell';

// hooks
import { useMemo } from 'react';

// types
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { Period } from 'components/shared/index';
import type { QualificationResponse } from 'RestAPIs/roster/qualifications/types';
import type { CostCentersFromOthersPrefValue } from '../useGetColumns.types';

// utils
import dayjs from 'dayjs';

/**
 * The class names a day column carries. The header marks the weekend edges and the Monday that
 * starts a week group; the cell marks today, the weekend and the closing day of the range.
 */
const getDayHeaderClassName = ({
  date,
  isOpenRequests,
  isLastDayOfRange
}: {
  date: string;
  isOpenRequests: boolean;
  isLastDayOfRange: boolean;
}) => {
  const weekday = dayjs(date).day();
  const weekdayClassName =
    weekday === 6
      ? 'saturday-header'
      : weekday === 0
      ? 'sunday-header'
      : weekday === 1
      ? 'monday-header'
      : '';

  return `${isOpenRequests ? 'dates-columns' : ''} ${weekdayClassName}${
    isLastDayOfRange ? ' last-day-header' : ''
  }`;
};

const getDayCellClassName = ({
  date,
  isToday,
  isLastDayOfRange
}: {
  date: string;
  isToday: boolean;
  isLastDayOfRange: boolean;
}) => {
  const weekday = dayjs(date).day();
  const isWeekend = weekday === 6 || weekday === 0;

  return `day-cell${isToday ? ' today' : isWeekend ? ' weekend' : ''}${
    isLastDayOfRange ? ' last-day-cell' : ''
  }`;
};

/**
 * One column per displayed date, for both grids.
 *
 * Month view is the only period that caps the width: 50px fixed, so a month fits without
 * horizontal scrolling. The other periods flex from a 100px floor.
 */
const useDayColumns = ({
  datesToDisplay,
  period = 'month',
  isOpenRequests = false,
  qualificationsByCostCenters = [],
  costCenterFromOthersPrefValue
}: {
  datesToDisplay: string[];
  period: Period | null;
  isOpenRequests?: boolean;
  qualificationsByCostCenters?: QualificationResponse[];
  costCenterFromOthersPrefValue?: CostCentersFromOthersPrefValue;
}) => {
  return useMemo<GridColDef<GridValidRowModel>[]>(
    () =>
      datesToDisplay.map((date): GridColDef<GridValidRowModel> => {
        const isToday = dayjs(date).isSame(dayjs(), 'day');
        const isLastDayOfRange = dayjs(date).isSame(
          dayjs(datesToDisplay[datesToDisplay.length - 1])
        );

        return {
          field: date,
          headerName: dayjs(date).format('DD MMM'),
          flex: 1,
          minWidth: period === 'month' ? 50 : 100,
          maxWidth: period === 'month' ? 50 : undefined,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          filterable: false,
          pinnable: false,
          hideable: false,
          disableColumnMenu: true,
          headerClassName: getDayHeaderClassName({
            date,
            isOpenRequests,
            isLastDayOfRange
          }),
          cellClassName: getDayCellClassName({
            date,
            isToday,
            isLastDayOfRange
          }),
          renderHeader: () => <DayHeader date={date} isToday={isToday} />,
          renderCell: (params) => (
            <DayCell
              params={params}
              date={date}
              isOpenRequests={isOpenRequests}
              qualificationsByCostCenters={qualificationsByCostCenters}
              costCenterFromOthersPrefValue={costCenterFromOthersPrefValue}
            />
          )
        };
      }),
    [
      datesToDisplay,
      period,
      isOpenRequests,
      qualificationsByCostCenters,
      costCenterFromOthersPrefValue
    ]
  );
};

export default useDayColumns;
